import { useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import LanguageSwitcher from "@/components/LanguageSwitcher";
import {
  trackLandingView,
  trackLandingCTAClick,
  trackLandingScrollDepth,
  trackRegisterButtonClick,
} from "@/utils/analytics";

function LandingPage() {
  const navigate = useNavigate();
  const { t } = useTranslation();

  // Fire once on mount + wire scroll depth milestones (25/50/75/100%).
  useEffect(() => {
    trackLandingView({ page: "landing" });
    const milestones = [25, 50, 75, 100];
    const hit = new Set();
    const onScroll = () => {
      const doc = document.documentElement;
      const scrolled = (doc.scrollTop + window.innerHeight) / doc.scrollHeight;
      const pct = Math.round(scrolled * 100);
      milestones.forEach((m) => {
        if (pct >= m && !hit.has(m)) {
          hit.add(m);
          trackLandingScrollDepth({ page: "landing", depth: m });
        }
      });
    };
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const goRegister = (role, ctaId, ctaLabel) => {
    trackLandingCTAClick({
      page: "landing",
      ctaId,
      ctaLabel,
      destination: `/register?role=${role}`,
    });
    trackRegisterButtonClick({ page: "landing", role });
    navigate(`/register?role=${role}`);
  };

  const handleKeyDown = (e, path) => {
    if (e.key === "Enter" || e.key === " ") {
      e.preventDefault();
      navigate(path);
    }
  };

  const features = [
    { icon: "💡", titleKey: "f1Title", descKey: "f1Desc" },
    { icon: "👥", titleKey: "f2Title", descKey: "f2Desc" },
    { icon: "💰", titleKey: "f3Title", descKey: "f3Desc" },
    { icon: "🎨", titleKey: "f4Title", descKey: "f4Desc" },
    { icon: "🤝", titleKey: "f5Title", descKey: "f5Desc" },
    { icon: "🛠️", titleKey: "f6Title", descKey: "f6Desc" },
    { icon: "🎤", titleKey: "f7Title", descKey: "f7Desc" },
    { icon: "🤖", titleKey: "f8Title", descKey: "f8Desc" },
    { icon: "🏛️", titleKey: "f9Title", descKey: "f9Desc" },
  ];

  return (
    <div className="landing-hero">
      {/* Language Switcher */}
      <div className="absolute top-4 right-4 z-10">
        <div className="bg-yellow-500 rounded-lg p-1">
          <LanguageSwitcher />
        </div>
      </div>

      <a href="#main-content" className="skip-link">Skip to main content</a>

      <main id="main-content" className="container mx-auto px-4 py-12 sm:py-16">
        {/* HERO */}
        <header className="text-center mb-14 animate-fade-in">
          <img
            src="/assets/lsbp-logo.png"
            alt="The Lemonade Stand Business Plan — Youth Entrepreneurship Program"
            className="mx-auto mb-6"
            style={{ maxWidth: "300px", height: "auto" }}
          />
          <div
            className="inline-block bg-yellow-100 text-yellow-800 border border-yellow-300 px-4 py-1.5 rounded-full text-sm font-semibold mb-5"
            data-testid="audience-badge"
          >
            🎯 {t("landing.audienceBadge")}
          </div>
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-gray-800 mb-4 leading-tight">
            {t("landing.title")}
            <br />
            <span className="text-yellow-600">{t("landing.subtitle")}</span>
          </h1>
          <p className="text-base sm:text-lg lg:text-xl text-gray-600 max-w-3xl mx-auto leading-relaxed">
            {t("landing.description")}
          </p>
        </header>

        {/* TWO PATHWAYS */}
        <section
          aria-labelledby="paths-heading"
          className="max-w-5xl mx-auto mb-16"
          data-testid="paths-section"
        >
          <h2
            id="paths-heading"
            className="text-2xl sm:text-3xl font-bold text-gray-800 text-center mb-8"
          >
            {t("landing.pathsHeading")}
          </h2>
          <div className="grid md:grid-cols-2 gap-6">
            <article
              className="bg-gradient-to-br from-purple-50 to-pink-50 border-2 border-purple-200 rounded-2xl p-6 sm:p-8 hover-lift"
              data-testid="path-middle-school"
            >
              <div className="text-4xl mb-3" aria-hidden="true">🎒</div>
              <div className="text-xs font-bold text-purple-700 bg-purple-100 inline-block px-2 py-0.5 rounded-full mb-2">
                {t("landing.pathMSGrades")}
              </div>
              <h3 className="text-2xl font-bold text-gray-800 mb-2">
                {t("landing.pathMSTitle")}
              </h3>
              <p className="text-gray-700 text-sm leading-relaxed">
                {t("landing.pathMSDesc")}
              </p>
            </article>
            <article
              className="bg-gradient-to-br from-blue-50 to-indigo-50 border-2 border-blue-200 rounded-2xl p-6 sm:p-8 hover-lift"
              data-testid="path-high-school"
            >
              <div className="text-4xl mb-3" aria-hidden="true">🎓</div>
              <div className="text-xs font-bold text-blue-700 bg-blue-100 inline-block px-2 py-0.5 rounded-full mb-2">
                {t("landing.pathHSGrades")}
              </div>
              <h3 className="text-2xl font-bold text-gray-800 mb-2">
                {t("landing.pathHSTitle")}
              </h3>
              <p className="text-gray-700 text-sm leading-relaxed">
                {t("landing.pathHSDesc")}
              </p>
            </article>
          </div>
        </section>

        {/* ROLE SELECTION */}
        <section
          aria-labelledby="role-selection-heading"
          className="max-w-4xl mx-auto mb-16"
        >
          <h2 id="role-selection-heading" className="sr-only">Choose Your Role</h2>
          <div className="grid md:grid-cols-2 gap-8">
            {/* Student Card */}
            <article
              className="bg-white rounded-3xl shadow-2xl p-8 role-card animate-slide-up stagger-1"
              onClick={() => goRegister("student", "landing-student-card", "I'm a Student")}
              onKeyDown={(e) => handleKeyDown(e, "/register?role=student")}
              tabIndex={0}
              role="button"
              aria-label="Get started as a student"
              data-testid="student-role-card"
            >
              <div className="text-6xl mb-4 text-center" aria-hidden="true">🎓</div>
              <h3 className="text-2xl sm:text-3xl font-bold text-gray-800 mb-4 text-center">
                {t("landing.imAStudent")}
              </h3>
              <p className="text-gray-600 mb-6 text-center">
                {t("landing.studentCardDesc")}
              </p>
              <button
                className="lemon-button w-full py-4 rounded-xl text-white font-bold text-lg"
                data-testid="get-started-student-btn"
                aria-label="Get Started as a Student"
              >
                {t("landing.getStarted")}
              </button>
            </article>

            {/* Educator Card */}
            <article
              className="bg-white rounded-3xl shadow-2xl p-8 role-card animate-slide-up stagger-2"
              onClick={() => goRegister("educator", "landing-educator-card", "I'm an Educator")}
              onKeyDown={(e) => handleKeyDown(e, "/register?role=educator")}
              tabIndex={0}
              role="button"
              aria-label="Get started as an educator"
              data-testid="educator-role-card"
            >
              <div className="text-6xl mb-4 text-center" aria-hidden="true">👩‍🏫</div>
              <h3 className="text-2xl sm:text-3xl font-bold text-gray-800 mb-4 text-center">
                {t("landing.imAnEducator")}
              </h3>
              <p className="text-gray-600 mb-6 text-center">
                {t("landing.educatorCardDesc")}
              </p>
              <button
                className="educator-button w-full py-4 rounded-xl text-white font-bold text-lg"
                data-testid="get-started-educator-btn"
                aria-label="Get Started as an Educator"
              >
                {t("landing.getStarted")}
              </button>
            </article>
          </div>
        </section>

        {/* WHAT STUDENTS BUILD (9 modules) */}
        <section aria-labelledby="features-heading" className="max-w-6xl mx-auto">
          <h2
            id="features-heading"
            className="text-2xl sm:text-3xl font-bold text-gray-800 text-center mb-10"
          >
            {t("landing.featuresHeading")}
          </h2>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6 sm:gap-8">
            {features.map((feature, index) => (
              <article
                key={feature.titleKey}
                className={`bg-white rounded-2xl p-6 shadow-lg hover-lift animate-slide-up stagger-${
                  (index % 3) + 1
                }`}
                data-testid={`feature-${feature.titleKey}`}
              >
                <div className="text-4xl mb-3" aria-hidden="true">{feature.icon}</div>
                <h3 className="text-lg font-bold text-gray-800 mb-2">
                  {t(`landing.features.${feature.titleKey}`)}
                </h3>
                <p className="text-gray-600 text-sm leading-relaxed">
                  {t(`landing.features.${feature.descKey}`)}
                </p>
              </article>
            ))}
          </div>
        </section>

        {/* FOOTER CTA */}
        <footer className="text-center mt-16">
          <p className="text-gray-600 mb-4">{t("landing.loginPrompt")}</p>
          <button
            onClick={() => navigate("/login")}
            className="text-yellow-600 hover:text-yellow-700 font-bold text-lg underline-offset-4 hover:underline focus:underline"
            data-testid="login-link"
            aria-label="Sign in to your existing account"
          >
            {t("landing.loginCta")}
          </button>
        </footer>
      </main>
    </div>
  );
}

export default LandingPage;
